import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Plus, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export default function TerminalReadersPanel() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("V12 Garage balie");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["terminal-readers"], queryFn: () => api.get("/terminal/readers").then((r) => r.data) });
  const readers = data?.readers || [];
  const unclaimed = data?.unclaimed || [];

  const add = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/terminal/readers/register", { registration_code: code.trim(), label });
      toast.success(t("terminalReaderAdded")); setCode("");
      qc.invalidateQueries({ queryKey: ["terminal-readers"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const claim = async (id) => {
    setBusy(true);
    try {
      await api.post(`/terminal/readers/${id}/claim`);
      toast.success(t("terminalReaderAdded"));
      qc.invalidateQueries({ queryKey: ["terminal-readers"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("terminalReaderRemoveConfirm"))) return;
    try { await api.delete(`/terminal/readers/${id}`); qc.invalidateQueries({ queryKey: ["terminal-readers"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-6 border-border" data-testid="terminal-readers-panel">
      <div className="flex items-center gap-2 mb-1"><CreditCard className="h-5 w-5 text-primary" />
        <div><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Stripe Terminal</div>
          <h3 className="font-display text-xl font-bold">{t("terminalReadersTitle")}</h3></div></div>
      <p className="text-sm text-muted-foreground mb-4">{t("terminalReadersHint")}</p>
      <div className="space-y-2 mb-4">
        {readers.length === 0 && <div className="text-sm text-muted-foreground" data-testid="terminal-readers-empty">{t("terminalNoReaders")}</div>}
        {readers.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3" data-testid={`terminal-reader-${r.id}`}>
            <div className="min-w-0"><div className="font-semibold text-sm truncate">{r.label}</div>
              <div className="text-[11px] font-mono text-muted-foreground">{r.device_type} · {r.serial || r.id}</div></div>
            <div className="flex items-center gap-2">
              <Badge className={r.status === "online" ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" : "bg-muted text-muted-foreground border-border"}>{r.status === "online" ? t("terminalOnline") : t("terminalOffline")}</Badge>
              <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`terminal-reader-remove-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      {unclaimed.length > 0 && (
        <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 p-3" data-testid="terminal-unclaimed">
          <div className="text-xs font-semibold mb-2">{t("terminalUnclaimedTitle")}</div>
          <div className="space-y-2">
            {unclaimed.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3" data-testid={`terminal-unclaimed-${r.id}`}>
                <div className="min-w-0"><div className="font-semibold text-sm truncate">{r.label || r.id}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">{r.device_type} · {r.serial || r.id} · {r.status === "online" ? t("terminalOnline") : t("terminalOffline")}</div></div>
                <Button size="sm" className="rounded-full" disabled={busy} onClick={() => claim(r.id)} data-testid={`terminal-claim-${r.id}`}><Link2 className="h-4 w-4 mr-1" />{t("terminalClaim")}</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={data?.test_mode ? "simulated-wpe" : t("terminalPairingCode")} data-testid="terminal-pairing-code" />
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("terminalLabel")} data-testid="terminal-label" />
        <Button onClick={add} disabled={busy || !code.trim()} className="rounded-full" data-testid="terminal-register-btn"><Plus className="h-4 w-4 mr-1" />{t("terminalRegister")}</Button>
      </div>
      {data?.test_mode && <div className="text-[11px] text-muted-foreground mt-2">{t("terminalTestModeHint")}</div>}
    </Card>
  );
}
